import { gsap, ScrollTrigger, Draggable } from "gsap/all";
import { _debounce } from ".";
gsap.registerPlugin(ScrollTrigger, Draggable);

class scrollBar {

  constructor(options) {

    this.initialized = false;
    this.bodyScroll = !options?.scroller; // Check if all document will be scrolling
    this.scroller = this._getScroller(options?.scroller);
    this.wrapper = this._getWrapper();
    this.posY = this.scroller.scrollTop;
    this.prevPosY = this.scroller.scrollTop;
    this.isScrolling = false;
    this.isDraggable = options?.draggable;
    this.smoothScroll = !options?.smoothScroll ? false : {
      speed: options?.smoothScroll?.speed ?? 250,
      smooth: options?.smoothScroll?.smooth ?? 12,
    };
    this.isMobile = options?.isMobile ?? 1024;
    this.isStarted = false;
    this.isDragging = false;
    this.isVisible = false;
    this.position = this.bodyScroll ? 'fixed' : 'absolute';

    this.bindedStart = this.start.bind(this);
    this.bindedPause = this.pause.bind(this);

    this.setup();
  }

  setup() {

    const refresh = _debounce(() => {
      // console.log('Updating scroll');
      if (window.innerWidth > this.isMobile) {
        this.init();
      } else {
        this.kill();
      }
    }, 200);

    const resizeObserver = new ResizeObserver(entries => refresh());
    resizeObserver.observe(this.wrapper);
  }

  init() {
    this.initialized = true;
    this.winHeight = window.innerHeight;
    this.wrapperHeight = (this.bodyScroll) ? this.winHeight : this.wrapper.clientHeight;
    this.scrollerHeight = this.scroller.scrollHeight;

    if (!this.bar) {
      this.bar = document.createElement('div');
      this.track = document.createElement('div');
      this.bar.classList.add('scroll-bar');
      this.bar.style.position = this.position;
      this.track.classList.add('scroll-track');
      this.bar.appendChild(this.track);
      this.wrapper.appendChild(this.bar);
      this.setTrackY = gsap.quickSetter(this.track, "y", "px");
      this.isDraggable && this.createDrag();
    }

    if (this.scrollerHeight > this.wrapperHeight) {
      this.trackHeight = this.wrapperHeight / (this.scrollerHeight / this.wrapperHeight);
      this.realScrollLength = this.scrollerHeight - this.wrapperHeight;
      this.trackPath = this.bar.clientHeight - this.trackHeight;
      this.track.style.height = `${this.trackHeight}px`;
      this.wrapper.addEventListener('mouseenter', this.bindedStart);
      this.wrapper.addEventListener('mouseleave', this.bindedPause);
      this.start();
    }
  }

  createDrag() {

    Draggable.create(this.track, {
      type: 'y',
      bounds: this.wrapper,
      onDragStart: () => {
        this.isDragging = true;
      },
      onDragEnd: () => {
        this.isDragging = false;
      },
      onDrag: (e) => {
        this.isVisible = true;
        this.isDragging = true;
        const { y } = this._getTranslate(this.track);

        let newPosY = y * this.realScrollLength / this.trackPath;
        this.posY = Math.max(0, Math.min(newPosY, this.realScrollLength));
      }
    });
  }

  updateScroll = () => {

    if (!this.initialized) return;

    this.wrapper.classList.toggle('is-scrolling', this.isScrolling);
    this.bar.classList.toggle('visible', this.isVisible);
    this.bar.classList.toggle('is-dragging', this.isDragging);

    // Move scroller on drag
    if (this.isDragging) {
      this.scroller.scrollTop = this.posY;
      return;
    }

    // Move scrollbar track on scroll
    if (!this.isDragging) {
      let offset = this.trackPath * (this.scroller.scrollTop / this.realScrollLength);
      this.setTrackY(offset);
    }

    // Smooth scrolling
    if (this.smoothScroll && this.isScrolling) {

      let offset = (this.posY - this.scroller.scrollTop) / this.smoothScroll.smooth;

      this.scroller.scrollTop += offset;

      if (this.prevPosY !== this.scroller.scrollTop) {
        this.prevPosY = this.scroller.scrollTop;
      } else {
        this.isScrolling = false;
      }
    }
  }

  scrollInside = (el) => {

  }

  scrollHandle = (e) => {

    if (!this.isStarted) return;

    // e.stopPropagation();
    // e.stopImmediatePropagation();

    this.isScrolling = true;
    this.isVisible = true;
    this.posY = this.scroller.scrollTop;

    // console.log(this.wrapper.scrollTop);
    // console.log(this.scroller);

    if (this.smoothScroll) {
      e.preventDefault();
      this.delta = this._getWheelDelta(e);
      let newPosY = this.scroller.scrollTop + (-this.delta * this.smoothScroll.speed);
      this.posY = Math.max(0, Math.min(newPosY, this.realScrollLength)); // limit scrolling
    }
  }

  start() {
    if (!this.isStarted && !this.isDragging) {
      // console.log('Started');
      this.isVisible = true;
      this.isStarted = true;
      this.wrapper.addEventListener('wheel', this.scrollHandle, { passive: false });
      gsap.ticker.add(this.updateScroll);
    }
  }

  pause() {
    if (this.isStarted && !this.isDragging) {
      // console.log('Paused');
      this.isVisible = false;
      this.isStarted = false;
      this.isScrolling = false;
      this.bar.classList.remove('visible');
      this.wrapper.removeEventListener('wheel', this.scrollHandle, { passive: false });
      gsap.ticker.remove(this.updateScroll);
    }
  }

  kill() {

    if (!this.initialized) return;
    // console.log('Kill');
    this.initialized = false;
    this.isVisible = false;
    this.isStarted = false;
    gsap.ticker.remove(this.updateScroll);
    this.wrapper.removeEventListener('mouseleave', this.bindedPause);
    this.wrapper.removeEventListener('mouseenter', this.bindedStart);
    this.wrapper.removeEventListener('wheel', this.scrollHandle, { passive: false });

    if (this.bar) {
      this.bar.remove();
      this.bar = null;
    }
  }

  scrollTo = (pos) => {
    this.posY = pos;
  }

  _getScroller(scroller) {

    switch (typeof scroller) {
      case 'object':
        return scroller;
      case 'string':
        return document.querySelector(scroller);
      default:
        return document.scrollingElement
          || document.documentElement
          || document.body.parentNode
          || document.body;
    }
  }

  _getWrapper() {

    if (this.bodyScroll) {
      return (this.scroller === document.body && document.documentElement)
        ? document.documentElement
        : this.scroller; // safari is the new IE
    }

    return this.scroller.parentNode;
  }

  _getTranslate(el) {
    const style = window.getComputedStyle(el);
    const matrix = style.transform || style.mozTransform;

    // No transform property. Simply return 0 values.
    if (matrix === 'none') {
      return { x: 0, y: 0 }
    }

    // Can either be 2d or 3d transform
    const matrixType = matrix.includes('3d') ? '3d' : '2d';
    const matrixValues = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');

    // 2d matrices have 6 values
    // Last 2 values are X and Y.
    if (matrixType === '2d') {
      return {
        x: matrixValues[4],
        y: matrixValues[5]
      }
    }

    // 3d matrices have 16 values
    // The 13th and 14th are X, Y
    if (matrixType === '3d') {
      return {
        x: matrixValues[12],
        y: matrixValues[13]
      }
    }
  }

  _getWheelDelta(e) {
    if (e.detail) {
      if (e.wheelDelta)
        return e.wheelDelta / e.detail / 40 * (e.detail > 0 ? 1 : -1) // Opera
      else
        return -e.detail / 3 // Firefox
    } else
      return e.wheelDelta / 120 // IE,Safari,Chrome
  }

}


export { scrollBar }